<template>
  <el-card shadow="never">
    <template slot="header">
      <el-breadcrumb separator-class="el-icon-arrow-right">
        <el-breadcrumb-item :to="{ name: $root.getDashboardPath() }">Dashboard</el-breadcrumb-item>
        <el-breadcrumb-item :to="{ name: hasAccess ? 'Risks' : 'Facility Risks' }">Risks</el-breadcrumb-item>
        <el-breadcrumb-item v-show="facility" :to="{ name: 'Facility Risks'}">
          {{ $root.getHeader() }} Risks
        </el-breadcrumb-item>
      </el-breadcrumb>
    </template>
    <el-row :gutter="20" class="upper">
      <el-col :span="24">
        <el-card shadow="never" class="box-card">
          <div slot="header">
            <span>
              Expiring Insurances
            </span>
          </div>
          <el-table
            ref="insuranceRisksTable"
            :data="customerInsurances"
            v-loading="insuranceLoading"
          >
            <el-table-column
              label="Customer"
              prop="customer.company"
              :width="300"
            >
              <template slot-scope="scope">
                <el-link type="primary" @click="() => routeToCustomer(scope.row.customer)">{{scope.row.customer.company}}</el-link>
              </template>
            </el-table-column>
            <el-table-column
              label="Email"
              prop="customer.email"
            >
            </el-table-column>
            <el-table-column
              label="Phone"
              prop="customer.phone"
            >
            </el-table-column>
            <el-table-column
              label="Expiry"
              :width="120"
            >
              <template slot-scope="scope">
                <span :class="scope.row.purged ? 'expired-insurance' : ''">
                  {{ scope.row.end | formatDate }}
                </span>
              </template>
            </el-table-column>
            <el-table-column label="Action" :width="150">
              <template slot-scope="scope">
                <el-button :loading="rowButtonLoading[scope.row.customer._id] === true" size="mini" @click="() => sendReminderEmail(scope.row)">
                  {{rowButtonLoading[scope.row.customer._id] ? 'Sending Reminder' : 'Send Reminder' }}
                </el-button>
              </template>
            </el-table-column>
            <el-table-column
              label="Last Reminder"
              :width="150"
              prop="lastReminder.createdAt"
              :formatter="(row, column, cellValue) => $options.filters.formatDate(cellValue, this.$root.state.facilityData) || '-'"
            >
            </el-table-column>
            <el-table-column
              label="Document"
              :width="100"
            >
              <template slot-scope="scope">
                <a :href="scope.row.url">
                  <el-button size="mini" icon="el-icon-download"></el-button>
                </a>
              </template>
            </el-table-column>
          </el-table>
        </el-card>
      </el-col>
    </el-row>
    <el-card shadow="never" class="box-card">
      <div slot="header" class="clearfix">
        <span>Expiring Forklift Certifications</span>
      </div>
      <el-table
        :data="customerEmployees"
        style="width: 100%"
        v-loading="loading"
        empty-text="No Data"
        @row-click="routeToCustomer"
      >
        <el-table-column
          label="Customer"
          prop="company"
        >
        </el-table-column>
        <el-table-column
          label="Email"
          prop="email"
        >
        </el-table-column>
        <el-table-column
          label="Employee name"
          prop="accounts.name"
        >
        </el-table-column>
        <el-table-column
          label="Employee Forklift Expiry"
        >
          <template slot-scope="scope">
            <span>
              {{ scope.row.accounts.forkLift.expiredAt | formatDate }}
            </span>
          </template>
        </el-table-column>
      </el-table>
    </el-card>
  </el-card>
</template>

<script>
import axios from 'axios';
import { RISKS_API_BASE } from '../env';

export default {
  name: 'Risks',
  async mounted() {
    if (!this.facility && !this.hasAccess) {
      this.$message.warning({
        title: 'Warning',
        message: 'User need to be super admin to view all facilities risks.',
      });
      this.loading = false;
      this.insuranceLoading = false;
      return;
    }
    await this.getData();

    this.loading = false;
  },
  props: {
    facility: { type: String },
  },
  computed: {
    hasAccess() {
      const roles = this.$keycloak.tokenParsed.realm_access.roles;
      if (roles.includes('level_1')) {
        return true;
      }
      return false;
    },
  },
  watch: {
    async facility() {
      if (!this.facility && !this.hasAccess) {
        this.$message.warning({
          title: 'Warning',
          message: 'User need to be super admin to view all facilities risks.',
        });
        this.loading = false;
        this.insuranceLoading = false;
        return;
      }
      await this.getData();
    },
  },
  data() {
    return {
      customerEmployees: [],
      customerInsurances: [],
      loading: true,
      rowButtonLoading: {},
      insuranceLoading: true,
    };
  },
  methods: {
    async getData() {
      this.insuranceLoading = true;
      await Promise.all([
        this.getEmployeeRisks(),
        this.getInsuranceRisks(),
      ]);

      this.insuranceLoading = false;
    },
    isExpired(insuranceEnd) {
      return Date.now() >= new Date(insuranceEnd);
    },
    async sendReminderEmail(row) {
      if (!this.facility) {
        this.$message.error({
          message: 'Please select a facility.',
        });
        return;
      }

      this.rowButtonLoading[row.customer._id] = true;
      this.rowButtonLoading = { ...this.rowButtonLoading };
      try {
        await axios.post(`${RISKS_API_BASE}/v1/insurance/${this.facility}/${row.customer._id}/expiry-email`, {
          emailTemplateName: 'insuranceEndPrior',
          createdBy: this.$keycloak.tokenParsed.email,
          insuranceUrl: row.url,
        }, {
          headers: { Authorization: `Bearer ${this.$keycloak.token}` }
        });

        this.$notify.success({
          title: 'Success',
          message: 'Successfully sent reminder email.',
        });
      } catch (err) {
        this.$notify.error({
          title: 'Error',
          message: 'There was an error while sending the email.',
        });
      }

      await this.getInsuranceRisks();
      this.rowButtonLoading[row.customer._id] = false;
    },
    async getEmployeeRisks() {
      try {
        this.loading = true;
        const endpoint = this.facility ? `/employees/${this.facility}` : '/employees';
        const response = await axios.get(`${RISKS_API_BASE}/v1${endpoint}`, {
          headers: {
            Authorization: `Bearer ${this.$keycloak.token}`,
          },
        });
        this.customerEmployees = response.data;
        this.loading = false;
        // eslint-disable-next-line no-console
      } catch (err) {
        this.loading = false;
      }
    },
    async getInsuranceRisks() {
      try {
        const endpoint = this.facility ? `/insurance/${this.facility}` : '/insurance';
        const response = await axios.get(`${RISKS_API_BASE}/v2${endpoint}`, {
          headers: {
            Authorization: `Bearer ${this.$keycloak.token}`,
          },
        });
        this.customerInsurances = response.data.expiring;
      } catch (err) {
        this.$message.error({
          title: 'Error',
          message: 'Failed to fetch expiring insurances.',
        });
        this.customerInsurances = [];
      }
    },
    routeToCustomer(row) {
      if (this.facility) {
        this.$router.push({
          name: 'Facility Customer',
          // eslint-disable-next-line no-underscore-dangle
          params: { id: row._id, facility: this.facility },
        });
        return;
      }
      // eslint-disable-next-line no-underscore-dangle
      this.$router.push({ name: 'Customer', params: { id: row._id } });
    },
  },
};
</script>

<style scoped>
.expired-insurance {
  color: red;
}
.upper {
  margin: 15px 0;
}

.button {
  margin: 0 5px;
  float: right;
}
</style>



// WEBPACK FOOTER //
// src/components/Risks.vue